/* A single scroll owner for the public header and collection category bar. */
(() => {
  const setup = () => {
    window.vnaScrollHeader?.abort();
    const controller = new AbortController();
    window.vnaScrollHeader = controller;
    const signal = controller.signal;
    const headers = [...document.querySelectorAll('.section-header-navigation, .section-header-mobile')];
    if (!headers.length) return;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const categories = [...document.querySelectorAll('[data-nail-series-layout] .vna-category-nav-wrap')];
    const legacyClasses = ['shopify-section-header-hidden', 'shopify-section-header-show', 'shopify-section-header-sticky', 'animate'];
    let header, height = 0, origin = 0, progress = 0, target = 0, frame = 0;
    let lastY = Math.max(0, scrollY), direction = 0, travel = 0, start = 0, from = 0;

    const paused = () => document.body.matches('.vna-desktop-menu-open, .menu_open, .menu-open, .vna-cart-drawer-open, .quickshop-popup-show, .quick-view-show, .quick_shop_popup_mobile, .vna-showcase-quick-modal-open, .vna-footer-localization-open, .sticky-search-open') ||
      document.documentElement.matches('.nail-filter-open, .vna-quick-modal-open, .vna-footer-localization-open') ||
      document.querySelector('[data-vna-language-popup]:not([hidden]), .vna-rec-modal.is-open, .search-modal[open]') ||
      [...(header?.querySelectorAll('details[open], predictive-search[open]') || [])].some(el => el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0) ||
      document.documentElement.style.overflow === 'hidden' || document.body.style.overflow === 'hidden';

    const paint = () => {
      if (!header) return;
      const shift = `${-height * progress}px`;
      header.style.setProperty('--vna-scroll-shift', shift);
      header.inert = progress >= .999;
      header.style.pointerEvents = progress >= .999 ? 'none' : '';
      categories.forEach(bar => {
        bar.style.setProperty('--vna-scroll-height', `${height}px`);
        bar.style.setProperty('--vna-scroll-shift', shift);
      });
    };
    const animate = time => {
      frame = 0;
      if (!start) start = time;
      const elapsed = Math.min(1, (time - start) / 220);
      progress = from + (target - from) * (1 - Math.pow(1 - elapsed, 3));
      paint();
      if (elapsed < 1) frame = requestAnimationFrame(animate);
    };
    const move = (next, immediate = false) => {
      if (next === target && !immediate) return;
      target = next;
      cancelAnimationFrame(frame);
      frame = 0;
      if (next === 0 && header) { header.inert = false; header.style.pointerEvents = ''; }
      if (immediate || reducedMotion.matches) { progress = next; paint(); return; }
      start = 0;
      from = progress;
      frame = requestAnimationFrame(animate);
    };
    const update = () => {
      const active = headers.find(el => getComputedStyle(el).display !== 'none' && el.offsetHeight > 0);
      if (!active) return;
      if (active !== header) {
        if (header) { header.inert = false; header.style.pointerEvents = ''; header.style.removeProperty('--vna-scroll-shift'); }
        header = active;
        progress = target = 0;
        cancelAnimationFrame(frame);
        frame = 0;
        direction = travel = 0;
      }
      headers.forEach(el => {
        el.classList.remove(...legacyClasses);
        el.style.removeProperty('top');
        el.dataset.vnaStickyOwner = 'global';
        el.dataset.vnaScrollHeader = '';
        el.querySelectorAll('sticky-header, sticky-header-mobile').forEach(native => {
          if (native.onScrollHandler) window.removeEventListener('scroll', native.onScrollHandler, false);
        });
      });
      height = header.offsetHeight;
      // A normal-flow marker preserves the header's original document position.
      origin = markerFor(header).getBoundingClientRect().top + scrollY;
      if (Math.max(0, scrollY) <= origin + height) move(0, true);
      else paint();
    };
    const markers = new Map();
    const markerFor = el => {
      if (markers.has(el)) return markers.get(el);
      const marker = document.createElement('span');
      marker.style.cssText = 'display:block;height:0;pointer-events:none';
      marker.setAttribute('aria-hidden', 'true');
      el.before(marker);
      markers.set(el, marker);
      return marker;
    };
    const onScroll = () => {
      const y = Math.max(0, Math.min(scrollY, Math.max(0, document.documentElement.scrollHeight - innerHeight)));
      const delta = y - lastY;
      lastY = y;
      if (paused()) { direction = travel = 0; return; }
      if (y <= origin + height) { direction = travel = 0; move(0); return; }
      if (!delta) return;
      const next = Math.sign(delta);
      travel = next === direction ? travel + Math.abs(delta) : Math.abs(delta);
      direction = next;
      if (travel >= 6) move(next > 0 && !header.contains(document.activeElement) ? 1 : 0);
    };
    categories.forEach(bar => { bar.dataset.vnaScrollCategory = ''; });
    update();
    const observer = new ResizeObserver(update);
    headers.forEach(el => observer.observe(el));
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    window.addEventListener('pageshow', update, { signal });
    reducedMotion.addEventListener('change', () => move(target, true), { signal });
    document.addEventListener('focusin', event => { if (header?.contains(event.target)) move(0, true); }, { signal });

    signal.addEventListener('abort', () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      markers.forEach(marker => marker.remove());
      headers.forEach(el => {
        el.inert = false;
        el.style.pointerEvents = '';
        el.style.removeProperty('--vna-scroll-shift');
        delete el.dataset.vnaScrollHeader;
        delete el.dataset.vnaStickyOwner;
      });
      categories.forEach(bar => {
        delete bar.dataset.vnaScrollCategory;
        bar.style.removeProperty('--vna-scroll-height');
        bar.style.removeProperty('--vna-scroll-shift');
      });
    }, { once: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, { once: true });
  else setup();
  document.addEventListener('shopify:section:load', setup);
})();
